/**
 * Pane Animation Timing State.
 *
 * This module manages the lifecycle of timing parameters during the animation build phase.
 * It is responsible for selecting the correct base timings for a given chart type (scenario),
 * holding those timings while they are refined and culled, and eventually "finalizing" them
 * into the baked output parameters that the runtime interpolation engine will use.
 *
 * The timing lifecycle has 3 main stages:
 * 1. Initialization: Create default working parameters based on animation style and scenario.
 * 2. Culling: Modify the working parameters to skip phases that have no real work to do.
 * 3. Finalization: Bake the working parameters into the resolved "shadow" properties after
 *    cross-pane gap removal and normalization.
 */
import { AnimationScenario } from './AnimationScenario';
import { AnimationStyle } from './AnimationStyle';
import { PaneChannelTiming } from './AnimationTimingTypes';
import { MarkAllAnimationParams } from './MarkAllAnimationParams';
import { MarkAllAnimationParamsManager } from './MarkAllAnimationParamsManager';
import { MarkAnimationTiming } from './MarkAnimationTiming';
import { PaneStateAttribute } from './PaneStateAttribute';
import { FractionWarperCurve as RuntimeFractionWarperCurve, WarpingFunction, WarpingFunctionMapUtil } from './WarpingFunctionMapUtil';

/**
 * Holds the resolved and unresolved timing state for a pane's animation.
 *
 * The "shadow" prefix is a convention from the C++ `tabanim` engine (e.g. `m_shadowTotalDuration`).
 * It indicates that these are internal, resolved, "baked" states computed during the build phase,
 * which are later exposed to the interpolation system to drive the actual UI animation.
 * They "shadow" the final parameters that the controller will run.
 */
export type PaneAnimationTimingState = {
  /** The animation scenario (e.g., Bar, Scatter, Other) determined by primitive type */
  shadowScenario: AnimationScenario;
  /** The pre-normalized total duration in slot units */
  shadowTotalDuration: number;
  /** Resolved timing for non-mark elements (e.g., axes, grid lines) */
  shadowNonMarksTiming: PaneChannelTiming | null;
  /** Attribute-specific timing overrides for non-marks */
  shadowNonMarkAttributeTimingOverrides: Map<PaneStateAttribute, PaneChannelTiming>;
  /** Resolved timing for entering marks */
  shadowEnterTiming: PaneChannelTiming | null;
  /** Resolved timing for moving marks */
  shadowMoveTiming: PaneChannelTiming | null;
  /** Resolved timing for exiting marks */
  shadowExitTiming: PaneChannelTiming | null;
  shadowEnterAttributeTimingOverrides: Map<PaneStateAttribute, PaneChannelTiming>;
  shadowMoveAttributeTimingOverrides: Map<PaneStateAttribute, PaneChannelTiming>;
  shadowExitAttributeTimingOverrides: Map<PaneStateAttribute, PaneChannelTiming>;
  /** The overall easing/warping function applied to the entire pane's transition */
  shadowOverallWarpingFunction: WarpingFunction | null;
  /** Transient unresolved working parameters, kept until the cross-pane finalize step */
  shadowWorkingParams: MarkAllAnimationParams | null;
};

export function toWarpingFunction(curve: string): WarpingFunction | null {
  return WarpingFunctionMapUtil.getWarpingFunction(curve as RuntimeFractionWarperCurve) || null;
}

export function toPaneChannelTiming(timing: MarkAnimationTiming): PaneChannelTiming {
  return {
    delay: timing.delay,
    duration: timing.duration,
    warpingFunction: toWarpingFunction(String(timing.warperCurve)),
  };
}

export function toAttributeTimingOverrides(overrideMap: Map<string, MarkAnimationTiming>): Map<PaneStateAttribute, PaneChannelTiming> {
  const result = new Map<PaneStateAttribute, PaneChannelTiming>();
  overrideMap.forEach((timing, attr) => {
    result.set(attr as PaneStateAttribute, toPaneChannelTiming(timing));
  });
  return result;
}

export function createPaneAnimationTimingState(scenario: AnimationScenario): PaneAnimationTimingState {
  return {
    shadowScenario: scenario,
    shadowTotalDuration: 0,
    shadowNonMarksTiming: null,
    shadowNonMarkAttributeTimingOverrides: new Map<PaneStateAttribute, PaneChannelTiming>(),
    shadowEnterTiming: null,
    shadowMoveTiming: null,
    shadowExitTiming: null,
    shadowEnterAttributeTimingOverrides: new Map<PaneStateAttribute, PaneChannelTiming>(),
    shadowMoveAttributeTimingOverrides: new Map<PaneStateAttribute, PaneChannelTiming>(),
    shadowExitAttributeTimingOverrides: new Map<PaneStateAttribute, PaneChannelTiming>(),
    shadowOverallWarpingFunction: null,
    shadowWorkingParams: null,
  };
}

export function createPaneWorkingParams(animationStyle: AnimationStyle, scenario: AnimationScenario): MarkAllAnimationParams {
  const manager = MarkAllAnimationParamsManager.getInstance();
  return animationStyle === AnimationStyle.Synced ? manager.getFlowAnimationParams(scenario) : manager.getAllAnimationParams(scenario);
}

export function finalizePaneAnimationTimingState(
  state: PaneAnimationTimingState,
  resolvedParams: MarkAllAnimationParams,
  totalDuration: number
): PaneAnimationTimingState {
  return {
    ...state,
    shadowTotalDuration: totalDuration,
    shadowOverallWarpingFunction: toWarpingFunction(String(resolvedParams.overallFractionWarperCurve)),
    shadowNonMarksTiming: toPaneChannelTiming(resolvedParams.nonMarksParams.overallTiming),
    shadowNonMarkAttributeTimingOverrides: toAttributeTimingOverrides(resolvedParams.nonMarksParams.attributeTimingOverrideMap),
    shadowEnterTiming: toPaneChannelTiming(resolvedParams.enterParams.overallTiming),
    shadowMoveTiming: toPaneChannelTiming(resolvedParams.moveParams.overallTiming),
    shadowExitTiming: toPaneChannelTiming(resolvedParams.exitParams.overallTiming),
    shadowEnterAttributeTimingOverrides: toAttributeTimingOverrides(resolvedParams.enterParams.attributeTimingOverrideMap),
    shadowMoveAttributeTimingOverrides: toAttributeTimingOverrides(resolvedParams.moveParams.attributeTimingOverrideMap),
    shadowExitAttributeTimingOverrides: toAttributeTimingOverrides(resolvedParams.exitParams.attributeTimingOverrideMap),
    shadowWorkingParams: null,
  };
}
